// The person page's cards below Access (AdminPerson.dc.html): Free things with the hold switch,
// Progress with Issue certificate, Logging in, Notes and History. On a phone, History, Referrals and This person fold
// to their headings, with a count where they have one (PhoneFold, the critique's admin 17).
import { staffFirstName } from '../../lib/names.js'
import { useState } from 'react'
import { usePhone } from '../../lib/media.js'
import { Button, Card, Dot, Switch, TextArea, TextInput, useAction, useAdmin } from '../ui/index.js'
import { PERSON, START_HERE_ANSWERS, byBoardOrder, freeLine, replayActivity, replayHistory, historyWithReplays, progressLine, progressPct, loginLine, historyLine, historyWhen, boardDay, firstOf } from './words.js'
import { olderHistory } from './data.js'
import { standingOf, progressLine as mmProgressLine, progressPct as mmProgressPct } from '../mastermind/words.js'

// ---- Free things: every account sees these; a hold hides 1 from this person, with a reason. The
// board flips the switch first and asks for the reason after; a hold can't be written without one, so
// switching off opens the reason box and the hold saves only once there's a reason (critic). Owner and
// admin hold and release; support sees the lines and the switches as they stand.
export function FreeThings({ data, role, first, onChanged }) {
  const { api } = useAdmin()
  const oa = role === 'owner' || role === 'admin'
  const free = data.catalogue.filter(p => p.access_kind === 'free' && p.state === 'live' && p.moved_in)
  const holds = new Map(data.holds.map(h => [h.product_id, h]))
  const webinars = new Map(data.webinars.map(w => [w.id, w]))
  // what they've done, with the replays watched (owner and admin read them; support's list is empty)
  const activity = [...data.activity, ...replayActivity(data.replays, webinars)]
  const [asking, setAsking] = useState(null) // product id whose reason box is open
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(null)
  const [error, setError] = useState(null)

  async function hold(p) {
    if (!reason.trim()) { setError({ id: p.id, message: PERSON.holdNeedsReason }); return }
    setBusy(p.id); setError(null)
    try {
      await api.hold_free({ person_id: data.person.id, product_id: p.id, reason })
      setAsking(null); setReason('')
      onChanged()
    } catch (e) { setError({ id: p.id, message: e.message }) } finally { setBusy(null) }
  }
  async function release(p, h) {
    setBusy(p.id); setError(null)
    try {
      await api.release_hold(h.id)
      onChanged()
    } catch (e) { setError({ id: p.id, message: e.message }) } finally { setBusy(null) }
  }

  return (
    <Card title={PERSON.free} aside={PERSON.freeAside} body={false} className="apl-free">
      {free.map(p => {
        const h = holds.get(p.id)
        const held = Boolean(h)
        const pending = asking === p.id
        const shown = !held && !pending
        return (
          <div key={p.id} className="apl-free-row">
            <div className="apl-free-top">
              <span className={`apl-free-text${held || pending ? ' apl-free-text--held' : ''}`}>
                <span className="apl-free-name">{p.name}</span>
                <span className="apl-free-line">{held ? PERSON.heldBack(first) : freeLine(p, activity, webinars)}</span>
                {held && <span className="apl-free-line">{PERSON.heldReason(h.reason)}</span>}
              </span>
              <Switch checked={shown} label={shown ? PERSON.holdLabel(p.name, first) : PERSON.showLabel(p.name, first)}
                stateLabel={shown ? PERSON.shown : PERSON.hidden} disabled={!oa || busy === p.id}
                onChange={on => {
                  if (!on) { setAsking(p.id); setReason(''); setError(null); return }
                  if (pending) { setAsking(null); return }
                  if (h) release(p, h)
                }} />
            </div>
            {pending && (
              <div className="apl-free-ask">
                <TextInput aria-label={PERSON.holdReasonLabel(p.name)} value={reason} onChange={setReason} placeholder={PERSON.holdReason} maxLength={500} autoFocus />
                <span className="ad-confirm-actions">
                  <Button kind="ink" size="sm" busy={busy === p.id} disabled={!reason.trim()} onClick={() => hold(p)}>{PERSON.holdSave}</Button>
                  <Button size="sm" onClick={() => { setAsking(null); setError(null) }}>{PERSON.holdCancel}</Button>
                </span>
              </div>
            )}
            {error?.id === p.id && <p className="ad-error" role="alert">{error.message}</p>}
          </div>
        )
      })}
      <p className="apl-free-foot">{PERSON.freeFoot(first)}</p>
    </Card>
  )
}

// ---- A replay's register pages (docs/replay-register-spec.md 5): the free account 1 made, "Free account since
// 23 Oct, from the register page for <replay title>.", and each replay they registered for, with when, the source
// and how many times. Owner and admin read the registrations (they're the lists with emails); support sees the
// free account line alone. Nothing to say, no card.
export function RegisterCard({ data }) {
  const { person } = data
  const registrations = data.registrations || []
  if (!person.free_account_at && !registrations.length) return null
  const titles = new Map((data.webinars || []).map(w => [w.id, w.title]))
  // the register page that made their free account, else the first they used
  const from = registrations.find(r => r.made_account) || registrations[0] || null
  return (
    <Card title={PERSON.registrations} aside={PERSON.registrationsAside} className="apl-register">
      {person.free_account_at && <p className="apl-register-line">{PERSON.freeAccount(boardDay(person.free_account_at), from ? (titles.get(from.webinar_id) || from.webinar_title) : null)}</p>}
      {registrations.length > 0 && (
        <ul className="apl-register-list">
          {registrations.map(r => (
            <li key={`${r.webinar_id}-${r.email}`}>{PERSON.registrationLine({
              title: titles.get(r.webinar_id) || r.webinar_title || 'A replay', when: boardDay(r.registered_at), source: r.source,
              times: r.times_registered, last: boardDay(r.last_registered_at),
            })}</li>
          ))}
        </ul>
      )}
    </Card>
  )
}

// ---- Progress, per product, with the gold bar; Issue certificate only where certificates are on
// (owner and admin; the Funding Engine on staging)
export function ProgressCard({ data, role, first, onIssued }) {
  const { api } = useAdmin()
  const oa = role === 'owner' || role === 'admin'
  // a member's Start here answers: owner, admin and support (a host reads none of them)
  const oas = oa || role === 'support'
  const products = new Map(data.catalogue.map(p => [p.id, p]))
  const rows = byBoardOrder(data.progress.filter(p => products.get(p.product_id)?.moved_in !== false)
    .map(p => ({ ...p, slug: products.get(p.product_id)?.slug, sort_order: products.get(p.product_id)?.sort_order })))
  const [issued, setIssued] = useState({})
  const issue = useAction(body => api.issue_certificate(body))

  async function go(p) {
    const answer = await issue.run({ person_id: data.person.id, product_id: p.product_id })
    if (answer) { setIssued(s => ({ ...s, [p.product_id]: answer })); onIssued(answer.message) }
  }
  return (
    <Card title={PERSON.progress} className="apl-progress">
      {!rows.length && <p className="apl-muted">{PERSON.noProgress(first)}</p>}
      {rows.map(p => {
        // a mastermind's line and bar come from its standing (the term month from payments taken)
        const st = standingOf(p.product_id, data.standing)
        const startHere = data.startHere?.[p.product_id] || null
        const pct = st ? mmProgressPct(st) : progressPct(p, startHere)
        const has = p.certificate_number || issued[p.product_id]
        return (
          <div key={p.product_id} className="apl-prog">
            <div className="apl-prog-head">
              <span className="apl-prog-name"><Dot product={{ slug: products.get(p.product_id)?.slug, look: p.look }} />{p.product_name}</span>
              <span className="apl-prog-pct">{pct}%</span>
            </div>
            <div className="apl-bar" role="progressbar" aria-label={`${p.product_name} progress`} aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
              <div className="apl-bar-fill" style={{ width: `${pct}%` }} />
            </div>
            <span className="apl-prog-line">
              <span>{st ? mmProgressLine(st) : progressLine(p, undefined, startHere)}</span>
              {oa && p.certificate_on && (
                <Button size="xs" busy={issue.busy} disabled={Boolean(has)} onClick={() => go(p)}>{has ? PERSON.issued : PERSON.issue}</Button>
              )}
            </span>
            {oas && data.startHereAnswers?.[p.product_id] && (
              <StartHereAnswers answers={data.startHereAnswers[p.product_id]} first={first} productId={p.product_id} />
            )}
          </div>
        )
      })}
      {issue.error && <p className="ad-error" role="alert">{issue.error.message}</p>}
    </Card>
  )
}

// ---- A member's Start here answers, read only, under the product's line (Adam, 7 Oct, the batch's
// question 2): the gate's ticks, the second channel and each assignment's ticks, as the screens name
// them. Nothing here changes anything; only the member ticks and picks.
function StartHereAnswers({ answers, first, productId }) {
  const W = START_HERE_ANSWERS
  const id = `apl-ans-${productId}`
  return (
    <section className="apl-ans" aria-labelledby={id}>
      <div className="apl-ans-top">
        <h3 className="apl-ans-h" id={id}>{W.title}</h3>
        <span className="apl-ans-aside">{W.aside(first)}</span>
      </div>
      {!answers.started
        ? <p className="apl-ans-none">{W.none(first)}</p>
        : (
          <>
            {answers.gate && <TickGroup group={answers.gate} />}
            {answers.channel && (
              <div className="apl-ans-group apl-ans-channel">
                <span className="apl-ans-name">{answers.channel.title}</span>
                <span className={answers.channel.label ? 'apl-ans-value' : 'apl-ans-count'}>{answers.channel.label || W.noChannel(first)}</span>
              </div>
            )}
            {answers.assignments.map((g, i) => <TickGroup key={`${g.title}-${i}`} group={g} />)}
          </>
        )}
    </section>
  )
}
function TickGroup({ group }) {
  const W = START_HERE_ANSWERS
  return (
    <div className="apl-ans-group">
      <div className="apl-ans-head">
        <span className="apl-ans-name">{group.title}</span>
        <span className="apl-ans-count">{W.count(group.ticked, group.items.length)}</span>
      </div>
      <ul className="apl-ans-list">
        {group.items.map((t, i) => (
          <li key={i} className={`apl-ans-item${t.ticked ? ' apl-ans-item--on' : ''}`}>
            <span className="apl-ans-mark" aria-hidden="true">
              {t.ticked && (
                <svg viewBox="0 0 12 12" width="10" height="10" focusable="false">
                  <path d="M2.5 6.2l2.3 2.3 4.7-4.9" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </span>
            <span className="apl-ans-text"><span className="sr-only">{t.ticked ? W.ticked : W.notTicked}: </span>{t.label}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

// ---- Logging in: how they log in, when they were last in, Resend login email (owner, admin, support)
// and Log out everywhere (owner, admin; an owner only for a member of staff)
export function LoginCard({ data, role, me }) {
  const { api } = useAdmin()
  const person = data.person
  const oa = role === 'owner' || role === 'admin'
  const oas = oa || role === 'support'
  const staffTarget = Boolean(person.role)
  const [sent, setSent] = useState(false)
  const [out, setOut] = useState(false)
  const resend = useAction(() => api.resend_login({ person_id: person.id }))
  const logOut = useAction(() => api.log_out_everywhere({ person_id: person.id }))
  const self = me?.who?.person_id === person.id
  return (
    <Card title={PERSON.login} className="apl-login">
      <p className="apl-login-line">{loginLine(person)}</p>
      {oas && !self && (
        <div className="apl-buttons">
          {(!staffTarget || role === 'owner') && (
            <Button size="sm" busy={resend.busy} disabled={sent} onClick={async () => { if (await resend.run()) setSent(true) }}>{sent ? PERSON.resent : PERSON.resend}</Button>
          )}
          {oa && (!staffTarget || role === 'owner') && (
            <Button size="sm" busy={logOut.busy} disabled={out} onClick={async () => { if (await logOut.run()) setOut(true) }}>{out ? PERSON.loggedOut : PERSON.logOut}</Button>
          )}
        </div>
      )}
      {(resend.answer || logOut.answer) && <p className="apl-done" role="status">{logOut.answer?.message || resend.answer?.message}</p>}
      {(resend.error || logOut.error) && <p className="ad-error" role="alert">{(resend.error || logOut.error).message}</p>}
    </Card>
  )
}

// ---- Notes: only staff see them (owner and admin); no edit or delete, as the board has
export function NotesCard({ data, onChanged }) {
  const { api } = useAdmin()
  const first = firstOf(data.person)
  const [text, setText] = useState('')
  const save = useAction(body => api.add_note(body))
  const authors = new Map(data.authors.map(a => [a.id, a]))
  return (
    <Card title={PERSON.notes} className="apl-notes">
      <span className="apl-notes-line">{PERSON.notesLine}</span>
      <TextArea aria-label={PERSON.noteLabel} value={text} onChange={setText} placeholder={PERSON.notePlaceholder(first)} rows={3} maxLength={4000} />
      <span>
        <Button kind="ink" size="sm" busy={save.busy} onClick={async () => {
          if (!text.trim()) return
          if (await save.run({ person_id: data.person.id, body: text })) { setText(''); onChanged() }
        }}>{PERSON.saveNote}</Button>
      </span>
      {save.error && <p className="ad-error" role="alert">{save.error.message}</p>}
      {data.notes.map(n => {
        const a = authors.get(n.author_id)
        return (
          <div key={n.id} className="apl-note">
            <span className="apl-note-text">{n.body}</span>
            <span className="apl-note-meta">{[staffFirstName(a), boardDay(n.created_at)].filter(Boolean).join(', ')}</span>
          </div>
        )
      })}
    </Card>
  )
}

// ---- History: everything, newest first, in words; Show older pages back
// On a phone, a card folded to its heading, with its count where it has one ("History (24)"). The card keeps its own
// heading for its name, out of sight inside the fold (admin-people.css). At laptop width the fold is open with its
// heading hidden, so it's the card alone; the fold is always there, so a change of width never draws the card afresh
// and loses what's open in it.
export function PhoneFold({ title, count, children }) {
  const phone = usePhone()
  return (
    <details className={`apl-fold${phone ? ' apl-fold--phone' : ''}`} open={!phone}>
      <summary className="apl-fold-head" hidden={!phone}><h2 className="ad-card-title apl-fold-title">{PERSON.withCount(title, count)}</h2></summary>
      {children}
    </details>
  )
}

// How many lines History shows from the first page, for its count in a phone's folded heading
export function historyCount(data, ctx) {
  const replays = replayHistory(data.replays, new Map(data.webinars.map(w => [w.id, w])))
  return historyWithReplays(data.history, replays, data.history.length < 100).filter(h => historyLine(h, ctx)).length
}

export function HistoryCard({ data, ctx }) {
  const { api } = useAdmin()
  const [more, setMore] = useState([])
  const [state, setState] = useState({ busy: false, done: data.history.length < 100, error: null })
  const all = [...data.history, ...more]
  // the replays watched go in their place (person_history doesn't hold them); the next page still
  // starts from person_history's own last row
  const replays = replayHistory(data.replays, new Map(data.webinars.map(w => [w.id, w])))
  const lines = historyWithReplays(all, replays, state.done).map(h => ({ h, line: historyLine(h, ctx) })).filter(x => x.line)
  async function older() {
    const last = all[all.length - 1]
    if (!last) return
    setState(s => ({ ...s, busy: true, error: null }))
    try {
      const rows = await olderHistory(api, data.person.id, last.happened_at)
      setMore(m => [...m, ...rows])
      setState({ busy: false, done: rows.length < 100, error: null })
    } catch (e) {
      setState({ busy: false, done: false, error: e })
    }
  }
  return (
    <Card title={PERSON.history} className="apl-history">
      {!lines.length && <p className="apl-muted">{PERSON.noHistory}</p>}
      <ol className="apl-hist">
        {lines.map(({ h, line }, i) => (
          <li key={`${h.happened_at}-${h.source}-${h.event}-${i}`} className="apl-hist-row">
            <span className="apl-hist-when">{historyWhen(h)}</span>
            <span className="apl-hist-dot" style={{ background: line.dot }} aria-hidden="true" />
            <span className="apl-hist-text">{line.text}</span>
          </li>
        ))}
      </ol>
      {!state.done && <span><Button size="sm" busy={state.busy} onClick={older}>{PERSON.older}</Button></span>}
      {state.error && <p className="ad-error" role="alert">{state.error.message}</p>}
    </Card>
  )
}
